import { CSSProperties, useMemo } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Link, useLocation } from "react-router";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, type TooltipContentProps, XAxis, YAxis } from "recharts";

import { ChartTooltip } from "nazare-ui/components/chart-tooltip";
import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";
import { Button } from "nazare-ui/primitives";
import { LoadingBar } from "../../../loading-bar";
import { workflowHistoryPercentageData, type WorkflowHistoryPoint, type WorkflowHistoryPercentagePoint } from "../../../workflow-history-percentage";
import styles from "./workflow-history-chart.module.css";
import { sentenceCaseWorkflowStatus } from "../../../workflow-step-filter";

export type WorkflowHistoryPeriod = "1h" | "24h" | "7d" | "30d" | "custom";
export const WORKFLOW_HISTORY_VIEW_TRANSITION = "wf-card-history";

const PERIODS: WorkflowHistoryPeriod[] = ["1h", "24h", "7d", "30d"];
const COLOURS = ["var(--blue)", "var(--green)", "var(--red)", "var(--orange)", "var(--purple)", "var(--nzr-muted)"];
const STATUS_COLOURS: Record<string, string> = { pending: "var(--nzr-muted)", running: "var(--blue)", completed: "var(--green)", failed: "var(--red)", cancelled: "var(--orange)" };

function isWorkflowHistoryPercentagePoint(value: unknown): value is WorkflowHistoryPercentagePoint {
  return typeof value === "object" && value !== null && "rawByStatus" in value && "total" in value;
}

export function WorkflowHistoryChart({ points, period, onPeriodChange, loading, error, warming, asOf, maximised = false, className, showPeriodControls = true }: { points: WorkflowHistoryPoint[]; period: WorkflowHistoryPeriod; onPeriodChange: (period: WorkflowHistoryPeriod) => void; loading?: boolean; error?: boolean; warming?: boolean; asOf?: string; maximised?: boolean; className?: string; showPeriodControls?: boolean }) {
  const location = useLocation();
  const statuses = useMemo(() => [...new Set(points.flatMap((point) => Object.keys(point.byStatus)))].sort(), [points]);
  const chartData = useMemo(() => workflowHistoryPercentageData(points), [points]);
  const height = maximised ? "100%" : 220;

  return (
    <PageSection
      className={`${styles.card}${maximised ? ` ${styles.maximisedCard}` : ""}${className ? ` ${className}` : ""}`}
      style={{ viewTransitionName: WORKFLOW_HISTORY_VIEW_TRANSITION } as CSSProperties}
      aria-label="Workflow status history"
      title="Workflow status history"
      description={asOf ? <span className="block text-[11px]">As of {new Date(asOf).toLocaleString("en-GB")}</span> : undefined}
      overlay={(loading || warming) && <LoadingBar />}
      actions={maximised ? (
        <PageSectionAction asChild aria-label="Minimise workflow status history" title="Minimise workflow status history">
          <Link to={`/workflows${location.search}`} viewTransition><Minimize2 aria-hidden="true" /></Link>
        </PageSectionAction>
      ) : (
        <PageSectionAction asChild aria-label="Expand workflow status history" title="Expand workflow status history">
          <Link to={`/workflows/history${location.search}`} viewTransition><Maximize2 aria-hidden="true" /></Link>
        </PageSectionAction>
      )}
    >
      {maximised && showPeriodControls && <div className="mb-3 flex flex-wrap gap-1" role="group" aria-label="History period">
        {PERIODS.map((option) => <Button key={option} variant={period === option ? "primary" : "outline"} aria-pressed={period === option} onClick={() => onPeriodChange(option)}>{option}</Button>)}
      </div>}
      {loading || warming ? <div className={styles.chartContent} aria-label="Loading workflow history" role="img"><svg width="100%" height={height} viewBox="0 0 640 220" preserveAspectRatio="none" aria-hidden="true"><path d="M0 55H640 M0 110H640 M0 165H640" stroke="var(--border-soft)" strokeWidth="1" /><path d="M0 180 L80 140 L160 155 L240 90 L320 125 L400 70 L480 105 L560 55 L640 80" fill="none" stroke="var(--border-soft)" strokeWidth="4" strokeDasharray="12 10" /></svg></div> : error ? <p className="py-8 text-center text-xs text-[var(--red)]">Unable to load workflow history. Retrying shortly.</p> : points.length === 0 ? <p className="py-8 text-center text-xs text-[var(--nzr-muted)]">No workflow history is available for this period.</p> : <div className={styles.chartContent} role="img" aria-label="Workflow status share by time bucket">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartData}>
            <CartesianGrid stroke="var(--border-soft)" strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" type="number" domain={["dataMin", "dataMax"]} stroke="var(--nzr-muted)" tick={{ fill: "var(--nzr-muted)" }} tickFormatter={(value: number) => new Date(value).toLocaleString("en-GB", { dateStyle: period === "30d" || period === "7d" ? "short" : undefined, timeStyle: "short" })} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDataOverflow tickFormatter={(value: number) => `${Math.round(value)}%`} stroke="var(--nzr-muted)" tick={{ fill: "var(--nzr-muted)" }} />
            <Tooltip
              content={(props: TooltipContentProps<number, string>) => (
                <ChartTooltip
                  {...props}
                  labelFormatter={(label) => (typeof label === "number" || typeof label === "string" ? new Date(label).toLocaleString("en-GB") : "")}
                  valueFormatter={(value, entry) => {
                    const point = isWorkflowHistoryPercentagePoint(entry.payload) ? entry.payload : undefined;
                    const status = entry.name ?? "";
                    const count = point?.rawByStatus[status.toLowerCase()] ?? 0;
                    return `${(value ?? 0).toFixed(1)}% (${count} runs)`;
                  }}
                  footer={(payload) => {
                    const point = isWorkflowHistoryPercentagePoint(payload[0]?.payload) ? payload[0].payload : undefined;
                    return `Total: ${point?.total ?? 0} runs`;
                  }}
                />
              )}
            />
            <Legend wrapperStyle={{ color: "var(--ink)" }} />
            {statuses.map((status, index) => <Area key={status} type="stepAfter" dataKey={status} name={sentenceCaseWorkflowStatus(status)} stackId="status" stroke={STATUS_COLOURS[status] ?? COLOURS[index % COLOURS.length]} fill={STATUS_COLOURS[status] ?? COLOURS[index % COLOURS.length]} fillOpacity={0.8} />)}
          </AreaChart>
        </ResponsiveContainer>
      </div>}
    </PageSection>
  );
}
